import React, { useState, useEffect, useCallback } from 'react';
import { getMyMessages, sendMessage } from './api';
import { useAuth } from './AuthContext';
import { Link as RouterLink } from 'react-router-dom';

// Material UI Imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import SendIcon from '@mui/icons-material/Send';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Chip from '@mui/material/Chip'; // <-- Import Chip
import Avatar from '@mui/material/Avatar';
import { alpha } from '@mui/system';
import Tooltip from '@mui/material/Tooltip';

// Helper function to format date (can reuse from other files)
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleString();
    } catch (e) {
        console.error("Error formatting date:", e);
        return 'Invalid Date';
    }
};

// Helper function to get Steam community image URL (can reuse from other files)
// Note: This assumes the backend Message object includes sender/receiver details like avatarUrl.
// If not, you might need to fetch user details separately or rely on AuthContext for 'me'.
// For now, we'll use placeholder or AuthContext user data.
const getSteamImageUrl = (iconUrlPath, sizeSuffix = '/64fx64f') => {
    if (!iconUrlPath) return '';
    if (iconUrlPath.startsWith('http')) {
        if (iconUrlPath.includes('community.cloudflare.steamstatic.com/economy/image/') && !iconUrlPath.match(/\/\d+fx\d+f/)) {
            return `${iconUrlPath}${sizeSuffix}`;
        }
        return iconUrlPath;
    }
    if (iconUrlPath.startsWith('//')) {
        return `https:${iconUrlPath}${sizeSuffix}`;
    }
    if (iconUrlPath.startsWith('ttps://')) {
        return `h${iconUrlPath}${sizeSuffix}`;
    }
    return `https://community.cloudflare.steamstatic.com/economy/image/${iconUrlPath}${sizeSuffix}`;
};

function MessagesPage() {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [replyContent, setReplyContent] = useState('');
    const [newTitle, setNewTitle] = useState(''); // For new messages to admin
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [selectedMessageToReply, setSelectedMessageToReply] = useState(null); // Track which message is being replied to

    // Set Document Title
    useEffect(() => {
        document.title = 'SpinnerCS - Messages';
    }, []);

    // Fetch messages
    const fetchMessages = useCallback(async () => {
        if (!user?.isAuthenticated) {
            setMessages([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const fetchedMessages = await getMyMessages();
            // Sort messages by timestamp, newest first
            const sortedMessages = fetchedMessages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

            // Check if the new data is different from the current state before updating
            // This prevents unnecessary re-renders if the polled data hasn't changed.
            const currentUnreadCount = messages.filter(m => !m.isRead).length;
            const newUnreadCount = sortedMessages.filter(m => !m.isRead).length;

            if (sortedMessages.length !== messages.length ||
                (sortedMessages.length > 0 && messages.length > 0 && sortedMessages[0].id !== messages[0].id) || // Check if newest message changed
                currentUnreadCount !== newUnreadCount // Check if unread count changed
            ) {
                setMessages(sortedMessages);
            } else {
                // console.log("Messages data unchanged, skipping state update.");
            }
        } catch (err) {
            console.error("Messages fetch error:", err.response?.data || err.message || err);
            // If a 500 error occurs, display empty messages instead of an error message
            if (err.response && err.response.status === 500) {
                setMessages([]);
                setError(null); // Clear any previous error state
                console.warn("Received 500 error from messages endpoint, displaying empty list.");
            } else {
                // For other errors (e.g., 401, 404, network issues), display a generic error message
                setError('Failed to load messages. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchMessages();
        // Optional: Set up polling to fetch new messages periodically
        const intervalId = setInterval(fetchMessages, 30000); // Fetch every 30 seconds
        return () => clearInterval(intervalId); // Cleanup interval on unmount
    }, [fetchMessages]);

    const handleReplyClick = (message) => {
        // Set the message we are replying to and clear the reply input
        setSelectedMessageToReply(message);
        setReplyContent('');
        setNewTitle(''); // Clear title if switching from new message to reply
        setSendError(null); // Clear previous send errors
        // Optionally scroll to the reply input area
        const replyInput = document.getElementById('reply-input');
        if (replyInput) {
            replyInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handleStartNewMessageToAdmin = () => {
        setSelectedMessageToReply({ isNewAdminMessage: true }); // Special object to indicate new message to admin
        setReplyContent('');
        setNewTitle('');
        setSendError(null);
        const replyInput = document.getElementById('new-message-title-input') || document.getElementById('reply-input');
        if (replyInput) {
            replyInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Focus the title input if it exists
            const titleInput = document.getElementById('new-message-title-input');
            if (titleInput) setTimeout(() => titleInput.focus(), 100);
        }
    };

    const handleCancelReply = () => {
        setSelectedMessageToReply(null);
        setReplyContent('');
        setNewTitle('');
        setSendError(null);
    };

    const handleSendMessage = async () => {
        if (!user?.isAuthenticated) return;

        let messagePayload = {};
        let isValid = false;

        if (selectedMessageToReply?.isNewAdminMessage) {
            if (!newTitle.trim() || !replyContent.trim()) {
                setSendError('Please enter a title and message content to contact support.');
                return;
            }
            messagePayload = {
                toUserId: 1, // Hardcoded Admin User ID
                content: replyContent.trim(),
                title: newTitle.trim(),
                replyToMessageId: null,
            };
            isValid = true;
        } else if (selectedMessageToReply) { // Replying to an existing message
            if (!replyContent.trim()) {
                setSendError('Please enter a message to reply.');
                return;
            }
            const recipientId = selectedMessageToReply.fromUserId === user.userId
                ? selectedMessageToReply.toUserId
                : selectedMessageToReply.fromUserId;
            const originalTitle = selectedMessageToReply.title || 'No Subject';
            const replyTitle = originalTitle.startsWith('Re: ') ? originalTitle : `Re: ${originalTitle}`;
            messagePayload = { toUserId: recipientId, content: replyContent.trim(), title: replyTitle, replyToMessageId: selectedMessageToReply.id };
            isValid = true;
        } else {
            setSendError('No message selected to reply to, and not composing a new message to admin.');
            return;
        }

        if (isValid) {
            setSending(true);
            setSendError(null);
            try {
                await sendMessage(messagePayload);
            setReplyContent(''); // Clear input on success
                setNewTitle(''); // Clear title input on success
            setSelectedMessageToReply(null); // Clear reply context
            // Refresh messages to show the new one
            await fetchMessages();
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.response?.data || err.message || 'Failed to send message.';
            setSendError(errorMessage);
            console.error("Send message error:", err.response?.data || err.message || err);
        } finally {
                setSending(false);
            }
        }
    };

    // Conditional Rendering
    if (!user?.isAuthenticated) {
        return <Alert severity="info" sx={{ mt: 4 }}>Please log in to view your messages.</Alert>;
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, minHeight: '200px' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading messages...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    }

    // Note: The current implementation lists all messages linearly.
    // A better UI would group messages by conversation.
    // For this basic version, we just list them.

    return (
        <Box sx={{ mt: 2, px: { xs: 1, sm: 2 } }}>
            <Typography variant="h4" gutterBottom align="center" sx={{ mb: 3 }}>
                Your Messages
            </Typography>

            {messages.length === 0 ? (
                <Alert severity="info" sx={{ justifyContent: 'center' }}>
                    You have no messages yet.
                </Alert>
            ) : (
                <Paper elevation={2} sx={{ bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden' }}>
                    <List>
                        {messages.map((message, index) => (
                            <React.Fragment key={message.id}>
                                <ListItem
                                    alignItems="flex-start"
                                    // Highlight unread messages
                                    sx={{
                                        bgcolor: message.isRead ? 'inherit' : alpha('#29b6f6', 0.1), // Highlight unread
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: alpha('#29b6f6', message.isRead ? 0.05 : 0.15),
                                        },
                                        borderLeft: selectedMessageToReply?.id === message.id ? '4px solid' : 'none',
                                        borderLeftColor: selectedMessageToReply?.id === message.id ? 'primary.main' : 'transparent',
                                        pl: selectedMessageToReply?.id === message.id ? 1.5 : 2,
                                    }}
                                    onClick={() => handleReplyClick(message)}
                                >
                                    <ListItemAvatar>
                                        <Avatar
                                            alt={
                                                Number(message.fromUserId) === Number(user.userId)
                                                    ? user.displayName || 'You'
                                                    : message.fromUserId === 1
                                                        ? 'SpinnerCS Support'
                                                        : `User ${message.fromUserId}`
                                            }
                                            src={
                                                Number(message.fromUserId) === Number(user.userId)
                                                    ? user.avatarUrl // Use avatar from AuthContext for current user
                                                    : null // No specific avatar for support or other users unless provided
                                            }
                                            sx={{
                                                bgcolor:
                                                    message.fromUserId === user.userId
                                                        ? 'primary.main' // This condition might also need Number() if it drives other logic, but for bgcolor it's likely fine.
                                                        : message.fromUserId === 1
                                                            ? 'info.main' // Distinct color for support
                                                            : 'secondary.main',
                                            }}
                                        >
                                            {/* Fallback character if src is not available or fails to load */}
                                            {!(message.fromUserId === user.userId && user.avatarUrl) && // Show fallback if not current user with avatar
                                                (Number(message.fromUserId) === Number(user.userId)
                                                    ? user.displayName?.[0]?.toUpperCase() || 'U'
                                                    : message.fromUserId === 1
                                                        ? 'S' // For Support
                                                        : '?') // Default for others
                                            }
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'bold' }}>
                                                    {Number(message.fromUserId) === Number(user.userId)
                                                        ? 'You'
                                                        : message.fromUserId === 1
                                                            ? 'SpinnerCS Support'
                                                            : `User ${message.fromUserId}`}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" component="span">
                                                    {formatDate(message.timestamp)}
                                                </Typography>
                                                {!message.isRead && message.toUserId === user.userId && (
                                                    <Chip label="Unread" color="primary" size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                                                )}
                                            </Stack>
                                        }
                                        secondary={
                                            <React.Fragment>
                                                {message.title && (
                                                    <Typography
                                                        sx={{ display: 'block', mt: 0.5 }}
                                                        component="span"
                                                        variant="body2"
                                                        color="text.primary"
                                                        fontWeight="medium"
                                                    >
                                                        {message.title}
                                                    </Typography>
                                                )}
                                                <Typography
                                                    sx={{ display: 'block', mt: 0.5 }}
                                                    component="span"
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {message.content}
                                                </Typography>
                                            </React.Fragment>
                                        }
                                    />
                                </ListItem>
                                {index < messages.length - 1 && <Divider component="li" />}
                            </React.Fragment>
                        ))}
                    </List>
                </Paper>
            )}

            {/* Reply Section */}
            <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2 }, mt: 4, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                    {selectedMessageToReply?.isNewAdminMessage
                        ? 'New Message to Support'
                        : selectedMessageToReply
                            ? `Replying to Message from User ${selectedMessageToReply.fromUserId}`
                            : 'Compose Message'}
                </Typography>

                {selectedMessageToReply && !selectedMessageToReply.isNewAdminMessage && (
                     <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha('#29b6f6', 0.05), borderLeft: '3px solid', borderLeftColor: 'primary.main', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            "{selectedMessageToReply.content.substring(0, 100)}{selectedMessageToReply.content.length > 100 ? '...' : ''}"
                        </Typography>
                        <Button size="small" onClick={handleCancelReply} sx={{ mt: 1 }}>Cancel Reply</Button>
                     </Box>
                )}
                {selectedMessageToReply?.isNewAdminMessage && (
                    <Button size="small" onClick={handleCancelReply} sx={{ mb: 2 }}>Cancel New Message</Button>
                )}

                {sendError && <Alert severity="error" sx={{ mb: 2 }}>{sendError}</Alert>}

                {!selectedMessageToReply && !user?.isAdmin && (
                    <Stack spacing={2} alignItems="center">
                        <Typography variant="body1" color="text.secondary">
                            Select a message above to reply, or contact support.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleStartNewMessageToAdmin}
                            startIcon={<ContactSupportIcon />}
                        >
                            Contact Support
                        </Button>
                    </Stack>
                )}

                {/* Show compose form if replying OR composing new admin message OR if user is admin (for future general compose) */}
                {(selectedMessageToReply || (user?.isAdmin && !selectedMessageToReply) /* Admin can compose new to anyone - future */) && (
                    <Stack spacing={2}>
                        {selectedMessageToReply?.isNewAdminMessage && (
                            <TextField
                                id="new-message-title-input"
                                fullWidth
                                label="Subject / Title"
                                variant="outlined"
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                                disabled={sending}
                                inputProps={{ maxLength: 100 }}
                                helperText={`${newTitle.length}/100 characters`}
                            />
                        )}
                        <TextField
                            id="reply-input"
                            fullWidth
                            multiline
                            rows={3}
                            label={selectedMessageToReply?.isNewAdminMessage ? "Your Message" : (selectedMessageToReply ? "Your Reply" : "Message Content")}
                            variant="outlined"
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            disabled={sending || (!selectedMessageToReply && !user?.isAdmin && !selectedMessageToReply?.isNewAdminMessage)}
                            inputProps={{ maxLength: 1000 }}
                            helperText={`${replyContent.length}/1000 characters`}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSendMessage}
                            disabled={
                                sending ||
                                !replyContent.trim() ||
                                (selectedMessageToReply?.isNewAdminMessage ? !newTitle.trim() : !selectedMessageToReply)
                            }
                            endIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                            sx={{ py: 1.5, px: 3, minWidth: '120px', mt: { xs: 1, sm: 0 } }}
                        >
                            {sending ? 'Sending...' : 'Send'}
                        </Button>
                    </Stack>
                )}

            </Paper>

        </Box>
    );
}

export default MessagesPage;